import type React from 'react'
import { useRef } from 'react'
import type { NavigationOptions } from '../navigation/navigate.js'

const FOCUSED_ATTR = 'data-keyboard-focused'

interface ListKeyboardNavItem {
  id: string
}

interface UseListKeyboardNavOptions<T extends ListKeyboardNavItem> {
  items: T[]
  onOpen?: (item: T, options: NavigationOptions) => void
}

/** Keyboard navigation for lists. Uses DOM attributes instead of React state to avoid re-rendering all items on each keypress. */
export const useListKeyboardNav = <T extends ListKeyboardNavItem>(options: UseListKeyboardNavOptions<T>) => {
  const { items, onOpen } = options
  const focusedIndexRef = useRef(-1)
  const itemRefs = useRef<Map<number, HTMLElement>>(new Map())

  const setFocused = (index: number) => {
    const prev = itemRefs.current.get(focusedIndexRef.current)
    prev?.removeAttribute(FOCUSED_ATTR)

    focusedIndexRef.current = index

    const next = itemRefs.current.get(index)
    if (next) {
      next.setAttribute(FOCUSED_ATTR, '')
      next.scrollIntoView({ block: 'nearest' })
    }
  }

  const handleKeyDown = (ev: React.KeyboardEvent) => {
    if (items.length === 0) {
      return
    }

    const prev = focusedIndexRef.current

    switch (ev.key) {
      case 'ArrowDown':
        ev.preventDefault()
        setFocused(prev < items.length - 1 ? prev + 1 : 0)
        break
      case 'ArrowUp':
        ev.preventDefault()
        setFocused(prev > 0 ? prev - 1 : items.length - 1)
        break
      case 'Enter':
        ev.preventDefault()
        if (prev >= 0 && prev < items.length && onOpen) {
          const navigationMode = ev.shiftKey ? 'dialog' : 'page'
          onOpen(items[prev], { navigationMode })
        }
        break
      case 'Home':
        ev.preventDefault()
        setFocused(0)
        break
      case 'End':
        ev.preventDefault()
        setFocused(items.length - 1)
        break
    }
  }

  const resetFocus = () => setFocused(-1)

  const getItemRef = (index: number) => (el: HTMLElement | null) => {
    if (el) {
      itemRefs.current.set(index, el)
    } else {
      itemRefs.current.delete(index)
    }
  }

  return { focusedIndexRef, resetFocus, listProps: { onKeyDown: handleKeyDown }, getItemRef }
}
